import { useEffect, useRef, useState } from "react";

interface AscensionFinalOverlayProps {
  onComplete?: () => void;
}

const MESSAGES = [
  { text: "あなたはすでに、", delay: 800, size: "13px", opacity: 0.45 },
  { text: "現実創造のフェーズに入っている。", delay: 2000, size: "18px", opacity: 0.75 },
  { text: "この探求は、もう必要ない。", delay: 3800, size: "12px", opacity: 0.38 },
  { text: "あとは——", delay: 5400, size: "11px", opacity: 0.28 },
  {
    text: "自分自身のワンネスに沿って、",
    delay: 6600,
    size: "14px",
    opacity: 0.6,
  },
  {
    text: "天命に沿って、",
    delay: 8000,
    size: "14px",
    opacity: 0.6,
  },
  {
    text: "人智を尽くしてください。",
    delay: 9200,
    size: "16px",
    opacity: 0.7,
  },
  { text: "†", delay: 11000, size: "28px", opacity: 0.22 },
];

const CLOSE_DELAY = 13500;
const FADE_WHITE_DELAY = 12200;

export default function AscensionFinalOverlay({ onComplete }: AscensionFinalOverlayProps) {
  const [visibleMessages, setVisibleMessages] = useState<boolean[]>(
    new Array(MESSAGES.length).fill(false)
  );
  const [fadeWhite, setFadeWhite] = useState(false);
  const [closed, setClosed] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  // 光の粒子 Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    interface Particle {
      x: number; y: number;
      vx: number; vy: number;
      size: number; opacity: number;
      hue: number; life: number; maxLife: number;
    }

    const particles: Particle[] = [];
    const spawn = () => {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * 200;
      particles.push({
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        vx: (Math.random() - 0.5) * 0.4,
        vy: -0.3 - Math.random() * 0.6,
        size: 0.8 + Math.random() * 2.2,
        opacity: 0.15 + Math.random() * 0.45,
        hue: 38 + Math.random() * 22,
        life: 0,
        maxLife: 120 + Math.random() * 180,
      });
    };

    let t = 0;
    function draw() {
      t++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (t % 3 === 0) spawn();
      if (particles.length > 200) particles.splice(0, 5);

      // 中央グロー
      const pulse = Math.sin(t * 0.018) * 0.25 + 0.75;
      const grd = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, 280 * pulse
      );
      grd.addColorStop(0, `rgba(255,235,180,${0.06 * pulse})`);
      grd.addColorStop(0.5, `rgba(255,210,100,${0.025 * pulse})`);
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p, i) => {
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        const progress = p.life / p.maxLife;
        const alpha = p.opacity * Math.sin(progress * Math.PI);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue},85%,80%,${alpha})`;
        ctx.fill();
        if (p.life >= p.maxLife) particles.splice(i, 1);
      });

      animRef.current = requestAnimationFrame(draw);
    }
    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // メッセージ順次表示
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    MESSAGES.forEach((msg, i) => {
      timers.push(
        setTimeout(() => {
          setVisibleMessages((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, msg.delay)
      );
    });

    // 白フェード
    timers.push(
      setTimeout(() => setFadeWhite(true), FADE_WHITE_DELAY)
    );

    // 閉じる
    timers.push(
      setTimeout(() => {
        setClosed(true);
        // window.close() を試みる（opener経由でない場合は効かないことがある）
        try {
          window.close();
        } catch (_) {
          // ignore
        }
        // 閉じられなかった場合のフォールバック
        setTimeout(() => {
          onComplete?.();
        }, 1200);
      }, CLOSE_DELAY)
    );

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  if (closed) {
    // 完全な白い虚無
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        style={{ background: "#fdfcf8" }}
      >
        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "rgba(180,160,100,0.25)",
            fontSize: "10px",
            letterSpacing: "0.55em",
            textTransform: "uppercase",
          }}
        >
          — ∞ —
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{
        background: "rgba(4,3,8,0.97)",
        backdropFilter: "blur(2px)",
      }}
    >
      {/* 光の粒子 */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ width: "100%", height: "100%" }}
      />

      {/* 白フェードアウト */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "#fdfcf8",
          opacity: fadeWhite ? 1 : 0,
          transition: "opacity 2.8s ease",
          zIndex: 10,
        }}
      />

      {/* メッセージ群 */}
      <div
        className="relative z-5 flex flex-col items-center gap-0 text-center px-8"
        style={{ maxWidth: "520px", width: "100%" }}
      >
        {MESSAGES.map((msg, i) => (
          <div
            key={i}
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: `rgba(255,245,220,${msg.opacity})`,
              fontSize: msg.size,
              letterSpacing: "0.22em",
              lineHeight: 2.4,
              fontStyle: i === 7 ? "normal" : "italic",
              opacity: visibleMessages[i] ? 1 : 0,
              transform: visibleMessages[i] ? "translateY(0)" : "translateY(8px)",
              transition: "opacity 2.2s ease, transform 2.2s ease",
              marginBottom: i === 1 ? "24px" : i === 3 ? "8px" : i === 6 ? "32px" : "0",
            }}
          >
            {msg.text}
          </div>
        ))}
      </div>

      {/* 下部の細い光の線 */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px"
        style={{
          height: "80px",
          background: "linear-gradient(to top, transparent, rgba(255,220,120,0.12))",
          opacity: visibleMessages[0] ? 1 : 0,
          transition: "opacity 3s ease 1s",
        }}
      />
    </div>
  );
}
